// The store's "would you review us on Google?" invite: where it sends the
// guest, and what this device remembers about it.
//
// The store admin pastes its own review link (denzo-admin > Store > Settings
// > Google review link) and the invite shows up once the meal is over — the
// bill has been printed, or the till has closed the order. Same feature, same
// settings and same endpoints as denzo-web's own components/review.

// Deliberately NOT one of STORAGE_KEYS: those are the QR session's keys and
// every one of them is wiped when the session expires (see
// enforceSessionExpiry). What a guest answered about reviewing has to outlive
// their meal, or the next scan at the same table would ask the same person
// all over again.
const KEY = "dso_google_review";

// Matches GoogleReviewService::SNOOZE_DAYS — how long "maybe later" holds the
// invite back for.
export const SNOOZE_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

// Mirrors GoogleReviewService::reviewUrl. The API normalizes this itself and
// its answer is what gets used when there is one; this is for a store whose
// API hasn't been deployed with the endpoints yet.
export function reviewUrlFrom(value) {
  const setting = String(value ?? "").trim();
  if (!setting) return "";
  // A link pasted straight out of Google — the Business Profile's own "Ask
  // for reviews" link, a Maps listing, a search result with its #lrd= review
  // anchor — is used exactly as it was given.
  if (/^https?:\/\//i.test(setting)) return setting;
  // A bare CID (the ludocid= / cid= number in a listing's own links) opens
  // the listing itself, where Reviews is one tap away.
  if (/^[0-9]{10,}$/.test(setting)) return `https://www.google.com/maps?cid=${setting}`;
  // Anything else is taken for a Place ID (ChIJ...), which Google's own
  // review form opens with directly.
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(setting)}`;
}

// settings.google_review: a store saved before the setting existed has no
// key, and counts as on — pasting a link is all it has to do.
export function isInviteOn(settings) {
  const flag = settings?.google_review;
  if (flag === undefined || flag === null || flag === "") return true;
  if (typeof flag === "string") return !["false", "0", "no", "off"].includes(flag.trim().toLowerCase());
  return !!flag;
}

export function storeInvites(store) {
  return isInviteOn(store?.settings) && !!reviewUrlFrom(store?.settings?.google_review_url);
}

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    const all = raw ? JSON.parse(raw) : null;
    return all && typeof all === "object" ? all : {};
  } catch {
    // private mode / blocked storage: nothing is remembered here, and the
    // API's own answer is what decides.
    return {};
  }
}

// { reviewed: true } once they were sent to Google, { laterAt: <ms> } after a
// "maybe later". Kept per store — unlike denzo-web this is one deployment
// shared by every store (see SessionContext).
export function readLocal(storeId) {
  return readAll()[String(storeId ?? "")] ?? {};
}

export function remember(storeId, patch) {
  const next = { ...readLocal(storeId), ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...readAll(), [String(storeId ?? "")]: next }));
  } catch {
    // kept for this page only
  }
  return next;
}

export function isSnoozed(local) {
  return !!local?.laterAt && Date.now() - Number(local.laterAt) < SNOOZE_DAYS * DAY_MS;
}
